<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        class="searchInput"
        size="large"
        state="default"
        placeholder="Référence facture / avoir, Numéro de dossier, SIRET..."
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />

      <div class="searchWrapper--actions">
        <BasicTooltip label="Exporter les factures">
          <BasicButton
            @click="exporter"
            size="large"
            icon-name="export"
            variant="outlined"
          />
        </BasicTooltip>
        <BasicButton
          v-if="readonlyRib === false"
          type="secondary"
          label="Déposer un RIB"
          size="large"
          @click="redirectSelfeRib"
        />
        <BasicButton
          v-if="readonlyFacture === false"
          label="Déposer une facture"
          size="large"
          @click="redirectSelfeFacture"
        />
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <div class="flex flex-gap-4">
        <BasicButton
          @click="exporter"
          label="Exporter"
          size="large"
          icon-name="export"
          variant="outlined"
        />
        <BasicButton
          v-if="readonlyFacture === false"
          @click="redirectSelfeFacture"
          label="Déposer une facture"
          icon-name="bill"
          size="large"
          width="full"
        />
      </div>
      <BasicButton
        v-if="readonlyRib === false"
        @click="redirectSelfeRib"
        label="Déposer un RIB"
        icon-name="document-text"
        type="secondary"
        size="large"
        width="full"
      />

      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        state="default"
        placeholder="Référence facture / avoir, Numéro de dossier..."
        :maxlength="80"
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdownMultiple
          v-model="statuts"
          label="Statut"
          :items="statutsItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="periodeDateReception"
          label="Période de réception"
          :items="periodeDateReceptionItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="periodeDateEmission"
          label="Période d'émission"
          :items="periodeDateEmissionItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!searchIsFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
              style="display: inline"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              @click="removeFilters"
              :disabled="!searchIsFiltered"
              type="reverse"
              iconName="filter-remove"
              style="display: inline"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="paginationNumberOfPages"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <FactureCardMobile
          v-for="(item, index) in store.pageData?.data"
          :key="index"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <FactureHeader v-model="order" />
        <FactureCardDesktop
          v-for="(item, index) in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="index"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Factures"
        headerText="Factures"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watchEffect } from 'vue'
  import { type FactureQueryDTO } from '@/features/api/extranet/dto/Facture/FactureQueryDTO'
  import { StatutFactureLibelle } from '@/features/api/extranet/dto/Facture/StatutFactureDTO'
  import { usePreferencesUtilisateur } from '@/features/application/preferences'
  import { PREFERENCES_UTILISATEUR_APP, type PreferenceGlobale } from '@/features/application/types'
  import { PeriodeLibelle } from '@/features/domaine/types/opco/shared/Periode'
  import { useImmerFieldPatcher } from '@/features/interface/composables'
  import { isObjectEquals } from '@/features/shared/tools'
  import { formatSearchQuery, parseSearchQuery } from '@/features/shared/tools/parse'
  import { useDialog } from '../../dialog'
  import { objectToDropdownItems } from '../../shared/tools'
  import { pageSizeItems } from '../dropdown-items'
  import type { PagedRequest } from '../storeRechercheBuilder'
  import { FactureCardDesktop, FactureCardMobile, FactureHeader } from './components'
  import { useRechercheFactureActions } from './useRechercheFactureActions'
  import { useRechercheFactureStore } from './useRechercheFactureStore'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import { navigateTo, type NavigateToQueryFactures, type WithNavigateToLocation } from '@/router/navigateTo'
  import { onBeforeRouteUpdate } from 'vue-router'

  const props = defineProps<WithNavigateToLocation<NavigateToQueryFactures>>()

  const store = useRechercheFactureStore()
  onBeforeUnmount(store.$reset)

  const prefService = usePreferencesUtilisateur<PreferenceGlobale>(PREFERENCES_UTILISATEUR_APP)
  const showFilters = ref(false)
  const statutsItems = objectToDropdownItems(StatutFactureLibelle)
  const periodeDateReceptionItems = objectToDropdownItems(PeriodeLibelle)
  const periodeDateEmissionItems = objectToDropdownItems(PeriodeLibelle)

  const { addFavori, redirectSelfeFacture, redirectSelfeRib, readonlyFacture, readonlyRib } =
    useRechercheFactureActions()

  const { setupFieldWithKey, setupFieldWithOptions, setupCustomUpdater } = useImmerFieldPatcher<
    PagedRequest<FactureQueryDTO>
  >({
    getModel: () => store.pageRequest!,
    setModel(newModel) {
      const queryChanged = !isObjectEquals(store.pageRequest?.query, newModel.query)
      if (queryChanged) {
        newModel = {
          ...newModel,
          pagination: {
            pageNumber: 1,
            pageSize: newModel.pagination.pageSize,
          },
        }
      }
      navigateTo({
        ...props.navigateToLocation,
        ...newModel,
      })
    },
    debounceDelay: 1000,
  })

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })

  const statuts = setupFieldWithKey<string[]>('query.statuts', true)
  const periodeDateReception = setupFieldWithKey<string>('query.periodeDateReception')
  const periodeDateEmission = setupFieldWithKey<string>('query.periodeDateEmission')
  const pageNumber = setupFieldWithKey<number>('pagination.pageNumber')

  const exporter = async () => {
    await store.printData()
    await useDialog().showDialog({
      type: 'Ok',
      title: 'Export des factures',
      message: 'Les factures ont été exportées avec succès, vous allez les recevoir par mail',
    })
  }

  const pageSize = setupFieldWithOptions<number | null | undefined>({
    getValue: (model) => model.pagination?.pageSize,
    setPatch: (patch, value) => {
      patch.pagination = {
        pageNumber: 1,
        pageSize: value!,
      }
      prefService.setPreferencesAsync({ pageSize: value! })
    },
  })

  const search = setupFieldWithOptions<string>({
    getValue: () => formatSearchQuery(store.pageRequest?.query.search ?? []),
    setPatch: (patch, value) => {
      if (!value) patch.query.search = []
      else patch.query.search = parseSearchQuery(value)
    },
    withDebounce: true,
  })

  const order = setupFieldWithOptions<string>({
    getValue: () => {
      return store.pageRequest?.orderBy?.at(0) ?? ''
    },
    setPatch: (patch, value) => {
      patch.orderBy = value ? [value] : []
      patch.pagination = {
        pageNumber: 1,
        pageSize: patch.pagination.pageSize,
      }
    },
  })

  const paginationNumberOfPages = computed(() => {
    if (store?.pageData?.totalRecords) {
      return Math.ceil(store?.pageData?.totalRecords / store.pageRequest!.pagination!.pageSize!)
    }
    return 0
  })

  const searchIsFiltered = computed(
    () =>
      search.value.length ||
      statuts.value.length ||
      periodeDateReception.value.length ||
      periodeDateEmission.value.length,
  )

  const removeFilters = setupCustomUpdater((pageRequest) => {
    pageRequest.query = {
      search: undefined,
      statuts: undefined,
      periodeDateEmission: undefined,
      periodeDateReception: undefined,
    }
    showFilters.value = false
  })

  const pageChange = (page: number) => {
    pageNumber.value = page
  }

  // Ouvre automatiquement la recherche avancée si des filtres avancés sont actifs
  watchEffect(() => {
    if (statuts.value?.length || periodeDateReception.value || periodeDateEmission.value) {
      showFilters.value = true
    }
  })
</script>

<style scoped lang="less">
  @import '../index.less';
</style>
